<template>
  <div class="action-needed">
    <div class="action-needed__hint">{{ hint }}</div>

    <div v-if="locked" class="action-needed__locked">
      <v-icon icon="mdi-lock-outline" size="16" class="action-needed__locked-icon" />
      <span>
        Your administrator has locked these settings in User Management. Please contact them to request changes.
      </span>
    </div>

    <div class="action-needed__list">
      <div v-if="loading" class="action-needed__empty">Loading…</div>
      <template v-else>
        <div
          v-for="list in lists"
          :key="list"
          class="action-needed__group"
          :role="sectioned ? 'group' : undefined"
          :aria-labelledby="sectioned ? `action-needed-${list}` : undefined">
          <div v-if="sectioned" :id="`action-needed-${list}`" class="action-needed__section">
            {{ LIST_LABELS[list] }}
          </div>
          <label v-for="opt in optionsByList[list]" :key="`${list}:${opt.key}`" class="action-needed__row">
            <v-switch
              :model-value="isSelected(list, opt)"
              :disabled="locked || saving"
              density="compact"
              hide-details
              color="primary"
              class="action-needed__switch"
              @update:model-value="toggle(list, opt, $event)" />
            <span class="action-needed__row-label">{{ labelFor(list, opt.key) }}</span>
          </label>
        </div>
      </template>
    </div>

    <div v-if="!locked" class="action-needed__save-bar">
      <v-btn variant="text" size="small" :disabled="saving" @click="resetToDefaults">Reset to Defaults</v-btn>
      <transition name="action-needed-fade">
        <span v-if="error" class="action-needed__msg action-needed__msg--error">{{ error }}</span>
        <span v-else-if="successMsg" class="action-needed__msg action-needed__msg--success">{{ successMsg }}</span>
      </transition>
      <v-spacer />
      <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { settingsService } from "@/services/settingsService";
import { useActionNeededStore, useRequirementsStore } from "@/stores";
import { tryParseApiErrors } from "@/utils";
import { SCOPE, CLIENT_CONFIG_SECTION, toBackendScope, isTableScope } from "@/constants/scope";
import { groupActionNeededOptions } from "@/constants/actionNeeded";
import { companyAudienceFor } from "@/constants/worklistSections";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
import { PA_STATUS_LABEL_BY_KEY } from "@/models/paStatus";
import { INTAKE_STATUS_LABEL_BY_KEY } from "@/models/intakeStatus";

// Derived from the same canonical maps the PA / Intake tables render, so Settings
// labels can never drift from the chips.
const LABELS = {
  pa: PA_STATUS_LABEL_BY_KEY,
  intake: INTAKE_STATUS_LABEL_BY_KEY,
};

const LIST_LABELS = { [SCOPE.PA]: "Prior Auth", [SCOPE.INTAKE]: "Intake" };
const TABLE_LABELS = {
  [SCOPE.PA]: "the Prior Auth table",
  [SCOPE.INTAKE]: "the Intake table",
  [SCOPE.WORKLIST]: "the Worklist",
};
const WORKLIST_LISTS = {
  all: [SCOPE.PA, SCOPE.INTAKE],
  prior_auth: [SCOPE.PA],
  intake: [SCOPE.INTAKE],
};

function errorText(err) {
  return err?.response ? tryParseApiErrors(err).join(" ") : err?.message || "Please try again.";
}

function humanizeKey(key) {
  return key
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: isTableScope,
  },
});

const actionNeededStore = useActionNeededStore();
const requirementsStore = useRequirementsStore();
const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();

const loading = ref(false);
const locked = ref(false);

// Snapshot of the server-persisted selection so we know when the UI is dirty.
const initial = ref({ intake: [], pa: [] });
// Local editable selection (raw status keys).
const selected = ref({ intake: [], pa: [] });
// Option catalog per scope (raw status keys).
const catalog = ref({ intake: [], pa: [] });

const lists = computed(() =>
  props.scope === SCOPE.WORKLIST
    ? WORKLIST_LISTS[companyAudienceFor(requirementsStore.visibleModules ?? [])]
    : [props.scope]
);

const sectioned = computed(() => lists.value.length > 1);

const optionsByList = computed(() =>
  Object.fromEntries(lists.value.map((list) => [list, groupActionNeededOptions(list, catalog.value[list])]))
);

const hint = computed(() => {
  const parts = [`Pick the statuses that count toward the "Action Needed" chip on ${TABLE_LABELS[props.scope]}.`];
  if (props.scope === SCOPE.WORKLIST) {
    const names = lists.value.map((list) => LIST_LABELS[list]).join(" and ");
    parts.push(
      lists.value.length > 1
        ? `These are the same lists as in the ${names} settings.`
        : `This is the same list as in the ${names} settings.`
    );
  }
  parts.push("These settings apply only to you.");
  return parts.join(" ");
});

const hasChanges = computed(() => lists.value.some(isDirty));

function isDirty(list) {
  const cur = [...selected.value[list]].sort();
  const base = [...initial.value[list]].sort();
  return JSON.stringify(cur) !== JSON.stringify(base);
}

function labelFor(list, key) {
  return LABELS[list]?.[key] || humanizeKey(key);
}

function isSelected(list, opt) {
  return opt.keys.some((key) => selected.value[list].includes(key));
}

function toggle(list, opt, enabled) {
  const cur = new Set(selected.value[list]);
  for (const key of opt.keys) {
    if (enabled) cur.add(key);
    else cur.delete(key);
  }
  // Preserve the option-catalog order so payload and UI stay aligned.
  const order = new Map(catalog.value[list].map((k, i) => [k, i]));
  selected.value = {
    ...selected.value,
    [list]: [...cur].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)),
  };
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const res = await settingsService.getMyActionNeeded();
    locked.value = res?.locked === true;
    const intakeSection = res?.[CLIENT_CONFIG_SECTION[SCOPE.INTAKE]] || {};
    const paSection = res?.[CLIENT_CONFIG_SECTION[SCOPE.PA]] || {};
    catalog.value = { intake: [...(intakeSection.options || [])], pa: [...(paSection.options || [])] };
    const intakeSelected = [...(intakeSection.statuses || [])];
    const paSelected = [...(paSection.statuses || [])];
    initial.value = { intake: intakeSelected, pa: paSelected };
    selected.value = { intake: [...intakeSelected], pa: [...paSelected] };
  } catch {
    error.value = "Failed to load action-needed settings.";
  } finally {
    loading.value = false;
  }
}

async function save() {
  const pending = lists.value.filter(isDirty).map((list) => [list, [...selected.value[list]]]);
  await runSave(async () => {
    const failures = [];
    for (const [list, payload] of pending) {
      try {
        const response = await settingsService.updateMyActionNeededStatuses(toBackendScope(list), payload);
        const savedList = Array.isArray(response?.statuses) ? response.statuses : payload;
        initial.value = { ...initial.value, [list]: [...savedList] };
        selected.value = { ...selected.value, [list]: [...savedList] };
      } catch (err) {
        failures.push(`${LIST_LABELS[list]} list not saved: ${errorText(err)}`);
      }
    }
    if (failures.length < pending.length) await actionNeededStore.reload();
    if (failures.length) throw new Error(failures.join(" "));
  });
}

async function resetToDefaults() {
  const shown = [...lists.value];
  const hidden = [SCOPE.PA, SCOPE.INTAKE].filter((list) => !shown.includes(list));
  await runSave(
    async () => {
      const failures = [];
      for (const list of shown) {
        try {
          await settingsService.resetMyActionNeeded(toBackendScope(list));
        } catch (err) {
          failures.push(`${LIST_LABELS[list]} list not reset: ${errorText(err)}`);
        }
      }
      if (failures.length < shown.length) await actionNeededStore.reload();
      const hiddenEdits = Object.fromEntries(hidden.map((list) => [list, selected.value[list]]));
      await load();
      selected.value = { ...selected.value, ...hiddenEdits };
      if (failures.length) throw new Error(failures.join(" "));
    },
    { successText: "Reset to defaults." }
  );
}

watch(
  () => props.scope,
  () => {
    error.value = "";
    successMsg.value = "";
  }
);

load();
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.action-needed {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-needed__hint {
  font-size: 12.5px;
  color: $lh_color_blue_gray;
  line-height: 1.45;
}

.action-needed__locked {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: $lh_color_snowy_white;
  font-family: $lh_font_family_dm_sans;
  font-size: 12.5px;
  color: $lh_color_navy_dark;
  line-height: 1.45;
}

.action-needed__locked-icon {
  color: $lh_color_blue_gray;
  margin-top: 1px;
}

.action-needed__list,
.action-needed__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.action-needed__section {
  font-family: $lh_font_family_dm_sans;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  padding: 12px 12px 4px;
}

.action-needed__group:first-child .action-needed__section {
  padding-top: 0;
}

.action-needed__empty {
  font-size: 13px;
  color: $lh_color_blue_gray;
  padding: 12px 0;
}

.action-needed__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.action-needed__switch {
  flex: 0 0 auto;
}

.action-needed__row-label {
  font-family: $lh_font_family_dm_sans;
  font-size: $lh_body3_1_size;
  color: $lh_color_navy_dark;
}

.action-needed__save-bar {
  display: flex;
  align-items: center;
  padding: 12px 0 8px;
  margin-bottom: -8px;
  border-top: 1px solid $lh_dash_border;
  position: sticky;
  bottom: -8px;
  background: white;
  z-index: 1;
}

.action-needed__msg {
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  margin-left: 12px;
}

.action-needed__msg--error {
  color: $lh_color_red;
}

.action-needed__msg--success {
  color: $lh_color_green;
}

.action-needed-fade-enter-active,
.action-needed-fade-leave-active {
  transition: opacity 160ms;
}

.action-needed-fade-enter-from,
.action-needed-fade-leave-to {
  opacity: 0;
}
</style>
